import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const LoginForm = ({ onClose }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    const newErrors = validateForm();
    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Mock successful login
      const mockUser = {
        id: '1',
        name: 'John Doe',
        email: formData?.email,
        type: formData?.email?.includes('admin') ? 'admin' : 'user',
      };

      localStorage.setItem('user', JSON.stringify(mockUser));

      // Redirect based on user type
      if (mockUser?.type === 'admin') {
        navigate('/admin-dashboard');
      } else {
        navigate('/customer-portal');
      }

      onClose?.();
    } catch (error) {
      setErrors({ general: 'Login failed. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDemoLogin = (email, password) => {
    setFormData({ email, password });
  };

  return (
    <div className="w-full max-w-md">
      {/* Demo Credentials Section */}
      <div className="mb-6 p-4 bg-muted/50 border border-border rounded-lg">
        <h4 className="text-sm font-semibold text-text-primary mb-3 flex items-center">
          <Icon name="Key" size={16} className="mr-2" />
          Demo Credentials
        </h4>
        <div className="space-y-2">
          <div className="flex items-center justify-between p-2 bg-white rounded border">
            <div>
              <span className="text-xs font-medium text-primary">Admin:</span>
              <span className="text-xs text-text-secondary ml-2">admin@clean360.com</span>
            </div>
            <Button
              size="xs"
              variant="ghost"
              onClick={() => handleDemoLogin('admin@clean360.com', 'admin123')}
              className="text-xs"
            >
              Use
            </Button>
          </div>
          <div className="flex items-center justify-between p-2 bg-white rounded border">
            <div>
              <span className="text-xs font-medium text-success">User:</span>
              <span className="text-xs text-text-secondary ml-2">user@clean360.com</span>
            </div>
            <Button
              size="xs"
              variant="ghost"
              onClick={() => handleDemoLogin('user@clean360.com', 'user123')}
              className="text-xs"
            >
              Use
            </Button>
          </div>
        </div>
        <p className="text-xs text-text-secondary mt-2">
          Click "Use" to auto-fill credentials, then click "Sign In"
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        {errors?.general && (
          <div className="p-3 bg-error/10 border border-error/20 rounded-lg">
            <p className="text-sm text-error">{errors?.general}</p>
          </div>
        )}

        <div>
          <Input
            type="email"
            name="email"
            placeholder="Enter your email"
            value={formData?.email}
            onChange={handleChange}
            error={errors?.email}
            disabled={isSubmitting}
            iconName="Mail"
          />
        </div>

        <div>
          <Input
            type="password"
            name="password"
            placeholder="Enter your password"
            value={formData?.password}
            onChange={handleChange}
            error={errors?.password}
            disabled={isSubmitting}
            iconName="Lock"
          />
        </div>

        <div className="flex items-center justify-between">
          <label className="flex items-center">
            <input
              type="checkbox"
              className="w-4 h-4 text-primary bg-white border-border rounded focus:ring-primary focus:ring-2"
            />
            <span className="ml-2 text-sm text-text-secondary">Remember me</span>
          </label>
          <Link
            to="/forgot-password"
            className="text-sm text-primary hover:text-primary-dark transition-colors"
          >
            Forgot password?
          </Link>
        </div>

        <Button
          type="submit"
          variant="default"
          fullWidth
          disabled={isSubmitting}
          iconName={isSubmitting ? undefined : "LogIn"}
          iconPosition="left"
        >
          {isSubmitting ? (
            <div className="flex items-center">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              Signing in...
            </div>
          ) : (
            'Sign In'
          )}
        </Button>

        <div className="text-center">
          <p className="text-sm text-text-secondary">
            Don't have an account?{' '}
            <Link
              to="/signup"
              className="text-primary hover:text-primary-dark transition-colors font-medium"
            >
              Sign up here
            </Link>
          </p>
        </div>
      </form>
    </div>
  );
};

export default LoginForm;